import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { ORIGENS_A_VER, type OrigemAVer, type StoreCredit } from '@core/types';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import CampoBusca from '../componentes/CampoBusca';
import Ordenacao from '../componentes/Ordenacao';
import SeletorCliente from '../componentes/SeletorCliente';
import {
  cancelarAVer, editarAVer, emitirAVer, enfileirarImpressao, listarAVer, usosDoAVer,
  type AVerComEmissor,
} from '../lib/consultas';
import { imprimirAVer, montarDocumentoAVer } from '../lib/aVerDoc';
import { baixarAVer } from '../lib/aVerPdf';
import { brl, dataBR, dataHoraBR, hojeISO, paraNumero } from '../lib/formato';
import { casaBusca, porData, porNumero, porTexto } from '../lib/listas';
import { mensagemDeErro } from '../lib/supabase';
import { useSessao } from '../lib/sessao';

// Tela 14 — "A ver": crédito de loja.
//
// O fluxo do balcão: cliente traz a peça estragada → gerente/dono emite o vale
// com o valor → imprime em duas vias → cliente assina → o crédito fica no
// sistema e o documento na mão. Na próxima compra, o vendedor digita o código
// na tela de Venda e o crédito abate do total.
//
// Regras que vêm do banco (não desta tela): saldo nunca sobe além do original,
// uso só via venda (ledger), cancelamento zera o saldo restante sem apagar o
// registro — o número impresso continua auditável para sempre.

type OrdemAVer = 'recente' | 'antigo' | 'maior' | 'menor' | 'cliente' | 'vence';

const ORDENS_AVER = [
  { id: 'recente', rotulo: 'Mais recente' },
  { id: 'antigo', rotulo: 'Mais antigo' },
  { id: 'maior', rotulo: 'Maior saldo' },
  { id: 'menor', rotulo: 'Menor saldo' },
  { id: 'vence', rotulo: 'Vence antes' },
  { id: 'cliente', rotulo: 'Cliente (A–Z)' },
] as const satisfies ReadonlyArray<{ id: OrdemAVer; rotulo: string }>;

export default function AVer() {
  const cliente = useQueryClient();
  const { perfil } = useSessao();
  const [emitindo, setEmitindo] = useState(false);
  const [aberto, setAberto] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<'ativos' | 'todos'>('ativos');
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState<OrdemAVer>('recente');

  const creditos = useQuery({ queryKey: ['a-ver'], queryFn: listarAVer });

  const visiveis = useMemo(() => {
    const filtrados = (creditos.data ?? []).filter((c) => (
      (filtro === 'todos' || (Number(c.saldo) > 0 && !c.cancelado_em))
      // O código é o que vem escrito no papel que o cliente traz de volta; o
      // documento é como se acha o vale de quem perdeu o papel.
      && casaBusca(busca, c.codigo, c.cliente_nome, c.cliente_doc, c.motivo)
    ));
    return [...filtrados].sort((a, b) => {
      if (ordem === 'antigo') return porData(a.emitido_em, b.emitido_em, 'asc');
      if (ordem === 'maior') return porNumero(Number(a.saldo), Number(b.saldo), 'desc');
      if (ordem === 'menor') return porNumero(Number(a.saldo), Number(b.saldo), 'asc');
      if (ordem === 'cliente') {
        return porTexto(a.cliente_nome, b.cliente_nome) || porData(a.emitido_em, b.emitido_em);
      }
      // Vale SEM vencimento (`valido_ate` nulo) cai no fim por porData: ele não
      // está correndo contra relógio nenhum, e não deve ocupar o topo de uma
      // lista cuja pergunta é "qual vence primeiro?".
      if (ordem === 'vence') return porData(a.valido_ate, b.valido_ate, 'asc');
      return porData(a.emitido_em, b.emitido_em);
    });
  }, [creditos.data, filtro, busca, ordem]);

  // O passivo é o da LOJA INTEIRA, e não segue a busca de propósito: é quanto
  // a loja deve em crédito, não quanto deve ao cliente que se digitou. Um
  // número que muda quando alguém filtra não serve para fechar o mês.
  const totalEmAberto = (creditos.data ?? [])
    .filter((c) => !c.cancelado_em)
    .reduce((s, c) => s + Number(c.saldo), 0);

  const filtrando = busca.trim() !== '';

  return (
    <Layout
      titulo={'Notas "a ver"'}
      acao={
        <button
          type="button"
          onClick={() => setEmitindo((v) => !v)}
          className="botao-primario px-3 py-2 text-xs"
        >
          {emitindo ? 'Fechar' : '+ Emitir'}
        </button>
      }
    >
      <div className="space-y-4">
        {emitindo && (
          <EmitirNovo
            emitidoPor={perfil?.nome ?? null}
            aoEmitir={() => {
              setEmitindo(false);
              cliente.invalidateQueries({ queryKey: ['a-ver'] });
            }}
          />
        )}

        <section className="cartao flex items-center justify-between gap-3 p-4">
          <div>
            <p className="rotulo">Crédito em aberto (passivo da loja)</p>
            <p className="text-xl font-black tabular-nums">{brl(totalEmAberto)}</p>
          </div>
          <div className="flex gap-2">
            {(['ativos', 'todos'] as const).map((f) => (
              <button key={f} type="button" onClick={() => setFiltro(f)}
                      aria-pressed={filtro === f}
                      className={[
                        'border px-3 py-2 text-xs font-bold capitalize',
                        filtro === f ? 'border-signal text-signal-soft' : 'border-edge text-dim',
                      ].join(' ')}>
                {f}
              </button>
            ))}
          </div>
        </section>

        <section className="cartao space-y-3 p-4">
          <CampoBusca
            valor={busca}
            aoMudar={setBusca}
            placeholder="Buscar por código, cliente, CPF/CNPJ ou motivo"
          />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Ordenacao opcoes={ORDENS_AVER} valor={ordem} aoMudar={setOrdem} />
            <p className="text-xs text-dim">
              {filtrando ? `${visiveis.length} de ${creditos.data?.length ?? 0}` : visiveis.length} nota(s)
            </p>
          </div>
        </section>

        {creditos.error ? <Aviso tipo="erro">{mensagemDeErro(creditos.error)}</Aviso> : null}
        {creditos.isLoading && <Carregando texto="Carregando notas" />}

        {visiveis.length > 0 ? (
          <ul className="space-y-2">
            {visiveis.map((c) => (
              <li key={c.id}>
                <CartaoAVer
                  credito={c}
                  aberto={aberto === c.id}
                  aoAbrir={() => setAberto(aberto === c.id ? null : c.id)}
                  aoMudar={() => cliente.invalidateQueries({ queryKey: ['a-ver'] })}
                />
              </li>
            ))}
          </ul>
        ) : (
          !creditos.isLoading && (
            <div className="cartao p-6 text-center text-sm text-mute">
              {filtrando && (creditos.data?.length ?? 0) > 0
                ? 'Nenhuma nota casa com essa busca.'
                : filtro === 'ativos'
                  ? 'Nenhum "a ver" com saldo. Emita pelo botão acima quando um cliente devolver peça.'
                  : 'Nenhum "a ver" emitido ainda.'}
            </div>
          )
        )}
      </div>
    </Layout>
  );
}

function EmitirNovo({ aoEmitir, emitidoPor }: {
  aoEmitir: () => void;
  /** Quem está logado — assina pela loja no documento impresso. */
  emitidoPor: string | null;
}) {
  const [nome, setNome] = useState('');
  // Vínculo com o cadastro (migração 024). Null é caso legítimo, não falta de
  // preenchimento: o nome digitado sozinho continua emitindo vale.
  const [clienteId, setClienteId] = useState<string | null>(null);
  const [doc, setDoc] = useState('');
  const [valor, setValor] = useState('');
  const [motivo, setMotivo] = useState('');
  // A ORIGEM (058). Sem padrão escolhido de propósito: o vocabulário fechado
  // só corrige o escopo se quem emite PARAR e dizer de onde veio o crédito.
  // Encomenda não está na lista — ver o cabeçalho da migração 058.
  const [origem, setOrigem] = useState<OrigemAVer | ''>('');
  const [validade, setValidade] = useState('');
  const [emitido, setEmitido] = useState<StoreCredit | null>(null);
  // Transcrição: vale que JÁ EXISTE no papel — o passivo antigo escrito à mão e
  // o que o balcão emitiu à caneta durante a falta de luz. Fechado por padrão
  // porque o caso comum é emitir agora; aberto, a data do papel manda.
  const [transcrevendo, setTranscrevendo] = useState(false);
  const [emitidoEm, setEmitidoEm] = useState('');
  // A fila é o caminho da impressora da loja; se ela falhar, o vale continua
  // válido e o PDF continua sendo a saída. Por isso o erro mora num estado
  // próprio, e não no erro da emissão.
  const [filaErro, setFilaErro] = useState<string | null>(null);
  // Para onde o papel vai. Antes o sistema fazia as DUAS coisas sempre: abria a
  // janela de impressão E mandava para a fila da loja — o que gera papel a mais
  // quando se queria só o arquivo, e nenhum controle quando o cliente pede o
  // documento pelo WhatsApp. Agora quem emite escolhe, e a escolha fica
  // lembrada durante a sessão da tela (o balcão repete o mesmo destino o dia
  // inteiro; perguntar de novo a cada vale seria atrito puro).
  const [destino, setDestino] = useState<'impressora' | 'pdf'>('impressora');

  const emitir = useMutation({
    mutationFn: async () => {
      const v = paraNumero(valor);
      if (!nome.trim()) throw new Error('Informe o nome do cliente.');
      if (v === null || v <= 0) throw new Error('Informe um valor maior que zero.');
      if (!origem) throw new Error('Escolha de onde vem o crédito: devolução, troca ou acordo.');
      if (transcrevendo && !emitidoEm) {
        throw new Error('Informe a data do papel que está sendo transcrito.');
      }
      const { codigo, credit_id } = await emitirAVer({
        cliente_nome: nome.trim(),
        valor: v,
        cliente_doc: doc.trim() || null,
        motivo: motivo.trim() || null,
        origem,
        valido_ate: validade || null,
        // Meio-dia local: a data pura viraria meia-noite UTC e, em Rio Grande
        // (UTC-3), cairia no dia ANTERIOR — a mesma armadilha de dataCurtaDeISO.
        emitido_em: transcrevendo && emitidoEm ? `${emitidoEm}T12:00:00` : null,
        customer_id: clienteId,
      });
      // monta o objeto para imprimir sem reconsultar
      const agora = new Date().toISOString();
      const novo: StoreCredit = {
        id: credit_id, numero: 0, codigo, unit_id: null,
        cliente_nome: nome.trim(), cliente_doc: doc.trim() || null,
        customer_id: clienteId,
        valor_original: v, saldo: v, motivo: motivo.trim() || null, origem,
        product_id: null, valido_ate: validade || null,
        emitido_por: null, cancelado_em: null, cancelado_por: null,
        motivo_cancelamento: null, created_at: agora,
        // O papel imprime a data do FATO: na transcrição é a do papel antigo,
        // na emissão normal é agora. Mesmo `coalesce` da RPC.
        emitido_em: emitidoEm ? `${emitidoEm}T12:00:00` : agora,
      };
      setEmitido(novo);
      setFilaErro(null);

      if (destino === 'pdf') {
        // Arquivo: nada de impressora. É o caminho de quem vai mandar o vale no
        // WhatsApp do cliente ou guardar para imprimir depois.
        baixarAVer(novo, { emissao: true, emitidoPor });
        return;
      }

      // Impressora da loja, pela fila (migração 022) — é o caminho que funciona
      // nos computadores que não têm a impressora instalada, que é o caso aqui.
      // Falhar não pode derrubar a emissão: o crédito JÁ existe no banco. O erro
      // vira aviso com uma saída ao lado, não exceção.
      try {
        await enfileirarImpressao({
          titulo: `Vale "a ver" ${codigo} — ${nome.trim()}`,
          documentoHtml: montarDocumentoAVer(novo, { emissao: true, emitidoPor }),
          refTable: 'store_credits',
          refId: credit_id,
        });
      } catch (e) {
        setFilaErro(mensagemDeErro(e));
      }
    },
    onSuccess: aoEmitir,
  });

  return (
    <section className="cartao space-y-4 border-signal/50 p-4">
      <h2 className="text-base font-bold">Emitir "a ver"</h2>

      {/* O mesmo seletor da Venda: procura no cadastro, cadastra na hora ou
          aceita só o nome digitado. Vincular é o que responde depois "esse
          cliente tem vale?" — com dois "João Silva" no caderno e um deles com
          R$ 80 a receber, procurar por nome é palpite. Continua OPCIONAL: vale
          transcrito de caderno antigo costuma não ter cadastro nenhum, e exigir
          um travaria justamente a regularização do passivo de papel. */}
      <div>
        <label className="rotulo">Cliente *</label>
        <SeletorCliente
          clienteId={clienteId}
          clienteNome={nome}
          aoEscolher={({ id, nome: n }) => { setClienteId(id); setNome(n); }}
        />
        <p className="mt-1 text-xs text-dim">
          O vale não é transferível — sai no nome, e o nome fica congelado no papel.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="rotulo" htmlFor="av-doc">CPF/CNPJ (opcional)</label>
          <input id="av-doc" className="campo font-mono" value={doc}
                 onChange={(e) => setDoc(e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="av-valor">Valor do crédito (R$) *</label>
          <input id="av-valor" className="campo text-lg" inputMode="decimal" value={valor}
                 onChange={(e) => setValor(e.target.value)} placeholder="0,00" />
        </div>
        <div>
          <label className="rotulo" htmlFor="av-validade">Validade (opcional)</label>
          {/* O piso da validade acompanha a data de emissão, não o dia de hoje:
              transcrever um vale de 2025 com validade de 2025 é legítimo, e é o
              que a RPC passou a aceitar (a_ver v2). Sem isto o campo recusaria
              justamente a transcrição correta. */}
          <input id="av-validade" type="date" className="campo" value={validade}
                 min={transcrevendo && emitidoEm ? emitidoEm : hojeISO()}
                 onChange={(e) => setValidade(e.target.value)} />
        </div>
      </div>

      {/* DE ONDE VEM O CRÉDITO (058). Três opções fechadas, e o aviso que
          corrige o desvio real: encomenda virava vale porque o balcão não
          tinha papel de encomenda para dar ao cliente. Agora tem. */}
      <div>
        <p className="rotulo">De onde vem o crédito *</p>
        <div className="grid gap-2 sm:grid-cols-3">
          {ORIGENS_A_VER.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => setOrigem(o.id)}
              aria-pressed={origem === o.id}
              className={`border px-3 py-2 text-left ${origem === o.id
                ? 'border-signal bg-signal/10' : 'border-edge'}`}
            >
              <span className="block text-sm font-bold">{o.rotulo}</span>
              <span className="block text-xs text-mute">{o.ajuda}</span>
            </button>
          ))}
        </div>
        <p className="mt-2 border-l-2 border-signal pl-2 text-xs text-mute">
          <strong className="text-paper">Peça encomendada não é "a ver".</strong> Vale é
          crédito de quem <em>devolveu</em> algo. Se o cliente pediu uma peça — pagando
          tudo, em parte ou nada —, isso é <Link to="/encomendas" className="underline">
          encomenda</Link>: ela imprime as duas vias com o pagamento e vira venda quando
          quitar.
        </p>
      </div>

      <div>
        <label className="rotulo" htmlFor="av-motivo">Referente a</label>
        <input id="av-motivo" className="campo" value={motivo}
               onChange={(e) => setMotivo(e.target.value)}
               placeholder="Ex.: devolução de amortecedor dianteiro com defeito" />
      </div>

      {/* Transcrição de papel. Existe por dois motivos reais: o passivo que já
          está na rua (vales escritos à mão antes do sistema) e a falta de luz,
          em que o balcão emite à caneta e lança depois. Nos dois casos a data
          que vale é a do papel — é ela que o documento imprime e que a validade
          referencia. O created_at do banco continua registrando quando entrou
          no sistema, então a auditoria enxerga a diferença. */}
      <div className="border border-edge p-3">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={transcrevendo}
                 onChange={(e) => setTranscrevendo(e.target.checked)} />
          <span>Transcrever vale de papel (vale antigo ou emitido na falta de luz)</span>
        </label>
        {transcrevendo && (
          <div className="mt-3">
            <label className="rotulo" htmlFor="av-emitido">Data do papel *</label>
            <input id="av-emitido" type="date" className="campo" value={emitidoEm}
                   max={hojeISO()} onChange={(e) => setEmitidoEm(e.target.value)} />
            <p className="mt-1 text-xs text-dim">
              O vale entra com a data em que foi escrito. Se ele já estiver vencido, entra
              vencido — o sistema registra a realidade, não a corrige. Guarde o papel
              original: ele é o comprovante da assinatura do cliente.
            </p>
          </div>
        )}
      </div>

      <div>
        <p className="rotulo">O que fazer com o documento</p>
        <div className="grid grid-cols-2 gap-2">
          {([
            ['impressora', 'Imprimir na loja', 'sai na impressora agora'],
            ['pdf', 'Salvar PDF', 'baixa o arquivo no aparelho'],
          ] as const).map(([id, titulo, ajuda]) => (
            <button
              key={id}
              type="button"
              onClick={() => setDestino(id)}
              className={`border px-3 py-2 text-left ${destino === id
                ? 'border-signal bg-signal/10' : 'border-edge'}`}
            >
              <span className="block text-sm font-bold">{titulo}</span>
              <span className="block text-xs text-mute">{ajuda}</span>
            </button>
          ))}
        </div>
        <p className="mt-1 text-xs text-mute">
          As duas geram o mesmo documento, com as 2 vias para assinar. O PDF é o que dá
          para mandar no WhatsApp do cliente ou imprimir em outro lugar depois.
        </p>
      </div>

      {emitir.error ? <Aviso tipo="erro">{mensagemDeErro(emitir.error)}</Aviso> : null}
      {emitido && !filaErro && (
        <Aviso tipo="ok">
          Emitido <strong className="font-mono">{emitido.codigo}</strong> —{' '}
          {destino === 'pdf'
            ? 'o PDF foi baixado (2 vias, cliente assina as duas).'
            : 'mandado para a impressora da loja (2 vias, cliente assina as duas).'}
        </Aviso>
      )}
      {emitido && filaErro && (
        <Aviso tipo="alerta">
          <p>
            O vale <strong className="font-mono">{emitido.codigo}</strong> foi emitido
            normalmente e o crédito já vale — o que falhou foi só a impressora da loja
            ({filaErro}).
          </p>
          <button type="button" className="botao-secundario mt-2"
                  onClick={() => baixarAVer(emitido, { emissao: true, emitidoPor })}>
            Salvar o PDF
          </button>
        </Aviso>
      )}

      <button type="button" className="botao-primario w-full" disabled={emitir.isPending}
              onClick={() => emitir.mutate()}>
        {emitir.isPending
          ? 'Emitindo…'
          : destino === 'pdf' ? 'Emitir e salvar PDF' : 'Emitir e imprimir na loja'}
      </button>
    </section>
  );
}

function CartaoAVer({
  credito, aberto, aoAbrir, aoMudar,
}: { credito: AVerComEmissor; aberto: boolean; aoAbrir: () => void; aoMudar: () => void }) {
  const [cancelando, setCancelando] = useState(false);
  const [editando, setEditando] = useState(false);
  const [motivo, setMotivo] = useState('');

  const cancelado = Boolean(credito.cancelado_em);
  const zerado = Number(credito.saldo) <= 0 && !cancelado;
  const vencido = !cancelado && credito.valido_ate
    ? credito.valido_ate < hojeISO()
    : false;

  const usos = useQuery({
    queryKey: ['a-ver-usos', credito.id],
    queryFn: () => usosDoAVer(credito.id),
    enabled: aberto,
  });

  const cancelar = useMutation({
    mutationFn: () => cancelarAVer(credito.id, motivo),
    onSuccess: () => { setCancelando(false); aoMudar(); },
  });

  // 2ª via pela fila. O documento é montado AGORA (saldo de hoje, tarja de
  // cancelado se for o caso) e congelado na linha — é reimpressão, e
  // reimpressão que mente sobre o saldo é briga de balcão.
  const paraImpressora = useMutation({
    mutationFn: () => enfileirarImpressao({
      titulo: `2ª via — vale ${credito.codigo} (${credito.cliente_nome})`,
      documentoHtml: montarDocumentoAVer(credito, { emitidoPor: credito.emitido_por_nome }),
      refTable: 'store_credits',
      refId: credito.id,
    }),
  });

  return (
    <article className={`cartao ${cancelado ? 'opacity-60' : ''}`}>
      <button type="button" onClick={aoAbrir}
              className="flex w-full items-center justify-between gap-3 p-4 text-left">
        <div className="min-w-0">
          <p className="font-mono text-sm font-bold text-signal-soft">{credito.codigo}</p>
          <p className="truncate font-semibold">{credito.cliente_nome}</p>
          {/* O nome do cartão é o CONGELADO, o que está no papel assinado. Só
              vale mostrar o cadastro quando ele diverge — foi corrigido depois
              da emissão —, senão é repetir a mesma linha duas vezes. */}
          {credito.cliente_cadastro_nome
            && credito.cliente_cadastro_nome !== credito.cliente_nome && (
            <p className="truncate text-xs text-mute">
              cadastro hoje: {credito.cliente_cadastro_nome}
            </p>
          )}
          <p className="text-xs text-dim">
            {credito.customer_id ? 'cliente vinculado · ' : ''}
            emitido {dataBR(credito.emitido_em)}
            {credito.valido_ate && ` · válido até ${dataBR(credito.valido_ate + 'T12:00:00')}`}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-lg font-black tabular-nums">{brl(Number(credito.saldo))}</p>
          <p className={`text-xs ${cancelado ? 'text-erro' : vencido ? 'text-alerta' : zerado ? 'text-dim' : 'text-ok'}`}>
            {cancelado ? 'cancelado' : vencido ? 'vencido' : zerado ? 'saldo esgotado' : `de ${brl(Number(credito.valor_original))}`}
          </p>
        </div>
      </button>

      {aberto && (
        <div className="space-y-3 border-t border-edge p-4">
          {credito.motivo && <p className="text-sm text-mute">Referente a: {credito.motivo}</p>}

          {usos.data && usos.data.length > 0 && (
            <div>
              <p className="rotulo">Usos</p>
              <ul className="divide-y divide-edge">
                {usos.data.map((u) => (
                  <li key={u.id} className="flex justify-between py-1.5 text-sm">
                    <span className="text-dim">{dataHoraBR(u.created_at)}</span>
                    <span className={`font-mono tabular-nums ${u.estornado_em ? 'text-dim line-through' : ''}`}>
                      −{brl(Number(u.valor))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {/* Reimpressão: o documento sai com o SALDO de hoje, não com o valor
                de emissão — dois papéis assinados somando mais crédito do que
                existe é briga de balcão garantida. */}
            <button type="button" className="botao-secundario flex-1"
                    onClick={() => imprimirAVer(credito, { emitidoPor: credito.emitido_por_nome })}>
              {cancelado ? '2ª via (cancelado)' : '2ª via (saldo atual)'}
            </button>
            {/* O mesmo documento pela FILA: é assim que sai papel num
                computador que não tem a impressora instalada. O texto diz o
                destino, não a tecnologia — "fila" não significa nada no balcão. */}
            <button type="button" className="botao-secundario flex-1"
                    disabled={paraImpressora.isPending}
                    onClick={() => paraImpressora.mutate()}>
              {paraImpressora.isPending ? 'Mandando…'
                : paraImpressora.isSuccess ? '✓ Mandado' : 'Imprimir na loja'}
            </button>
            {/* Arquivo, para mandar ao cliente ou imprimir fora daqui. Também
                monta o saldo de HOJE — é a mesma reimpressão, outro destino. */}
            <button type="button" className="botao-secundario flex-1"
                    onClick={() => baixarAVer(credito, { emitidoPor: credito.emitido_por_nome })}>
              Salvar PDF
            </button>

            {!cancelado && (
              <button type="button" className="botao-secundario flex-1"
                      aria-pressed={editando}
                      onClick={() => { setEditando((v) => !v); setCancelando(false); }}>
                {editando ? 'Fechar edição' : 'Editar'}
              </button>
            )}
            {!cancelado && Number(credito.saldo) > 0 && (
              !cancelando ? (
                <button type="button"
                        className="border border-edge px-4 py-3 text-sm font-bold text-erro"
                        onClick={() => setCancelando(true)}>
                  Cancelar
                </button>
              ) : (
                <div className="w-full space-y-2 border border-erro/40 bg-erro/5 p-3">
                  <p className="text-xs text-mute">
                    Cancelar zera o saldo restante ({brl(Number(credito.saldo))}). O registro fica
                    no sistema — o documento impresso continua identificável.
                  </p>
                  <input className="campo py-2 text-sm" value={motivo} autoFocus
                         placeholder="Motivo (obrigatório)"
                         onChange={(e) => setMotivo(e.target.value)} />
                  {cancelar.error ? <Aviso tipo="erro">{mensagemDeErro(cancelar.error)}</Aviso> : null}
                  <div className="flex gap-2">
                    <button type="button" className="botao-secundario flex-1 py-2 text-sm"
                            onClick={() => setCancelando(false)}>Voltar</button>
                    <button type="button"
                            className="botao-perigo flex-1 px-3 py-2 text-sm disabled:opacity-50"
                            disabled={cancelar.isPending || !motivo.trim()}
                            onClick={() => cancelar.mutate()}>
                      {cancelar.isPending ? 'Cancelando…' : 'Confirmar'}
                    </button>
                  </div>
                </div>
              )
            )}
          </div>

          {editando && !cancelado && (
            <EditarVale
              credito={credito}
              usado={(usos.data ?? [])
                .filter((u) => !u.estornado_em)
                .reduce((s2, u) => s2 + Number(u.valor), 0)}
              aoPronto={() => { setEditando(false); aoMudar(); }}
            />
          )}

          {cancelado && (
            <p className="text-xs text-erro">
              Cancelado em {dataBR(credito.cancelado_em!)}: {credito.motivo_cancelamento}
            </p>
          )}
        </div>
      )}
    </article>
  );
}

/**
 * Editar um vale emitido (decisão do dono, 11/08/2026: tudo editável).
 *
 * A única regra dura é aritmética e mora na RPC: o valor não pode ficar menor
 * que o que JÁ FOI USADO — emitiu 100, usou 50, o piso é 50. O saldo é
 * recalculado no banco (valor novo − usado), nunca digitado aqui.
 *
 * O que a edição NÃO faz: reescrever o papel que o cliente assinou. Mudou
 * valor ou nome, o documento na mão dele continua com os números antigos —
 * por isso o aviso de reimprimir e colher assinatura de novo.
 */
function EditarVale({ credito, usado, aoPronto }: {
  credito: AVerComEmissor;
  /** Soma dos usos não estornados — o piso do valor. */
  usado: number;
  aoPronto: () => void;
}) {
  const [nome, setNome] = useState(credito.cliente_nome);
  const [doc, setDoc] = useState(credito.cliente_doc ?? '');
  const [valor, setValor] = useState(String(credito.valor_original).replace('.', ','));
  const [motivo, setMotivo] = useState(credito.motivo ?? '');
  const [validade, setValidade] = useState(credito.valido_ate ?? '');
  const [clienteId, setClienteId] = useState<string | null>(credito.customer_id);
  const [nomeCadastro, setNomeCadastro] = useState(
    credito.cliente_cadastro_nome ?? credito.cliente_nome,
  );

  const salvar = useMutation({
    mutationFn: async () => {
      const v = paraNumero(valor);
      if (v === null || v <= 0) throw new Error('Informe um valor maior que zero.');
      await editarAVer(credito.id, {
        cliente_nome: nome.trim(),
        valor: v,
        cliente_doc: doc.trim() || null,
        motivo: motivo.trim() || null,
        valido_ate: validade || null,
        customer_id: clienteId,
      });
    },
    onSuccess: aoPronto,
  });

  const mudouDocumento = nome.trim() !== credito.cliente_nome
    || (paraNumero(valor) ?? 0) !== Number(credito.valor_original);

  return (
    <section className="space-y-3 border border-edge p-3">
      <p className="rotulo">Editar o vale {credito.codigo}</p>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="rotulo" htmlFor={`ed-nome-${credito.id}`}>Cliente (nome no vale) *</label>
          <input id={`ed-nome-${credito.id}`} className="campo" value={nome}
                 onChange={(e) => setNome(e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor={`ed-doc-${credito.id}`}>CPF/CNPJ</label>
          <input id={`ed-doc-${credito.id}`} className="campo font-mono" value={doc}
                 onChange={(e) => setDoc(e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor={`ed-valor-${credito.id}`}>Valor do crédito (R$) *</label>
          <input id={`ed-valor-${credito.id}`} className="campo" inputMode="decimal" value={valor}
                 onChange={(e) => setValor(e.target.value)} />
          {usado > 0 && (
            <p className="mt-1 text-xs text-alerta">
              Já foram usados {brl(usado)} — o valor não pode ficar abaixo disso. O saldo
              vira automaticamente o valor novo menos o usado.
            </p>
          )}
        </div>
        <div>
          <label className="rotulo" htmlFor={`ed-val-${credito.id}`}>Validade</label>
          <input id={`ed-val-${credito.id}`} type="date" className="campo" value={validade}
                 min={credito.emitido_em.slice(0, 10)}
                 onChange={(e) => setValidade(e.target.value)} />
          <p className="mt-1 text-xs text-mute">Vazio = sem vencimento.</p>
        </div>
      </div>

      <div>
        <label className="rotulo" htmlFor={`ed-motivo-${credito.id}`}>Referente a *</label>
        <input id={`ed-motivo-${credito.id}`} className="campo" value={motivo}
               onChange={(e) => setMotivo(e.target.value)} />
      </div>

      <div>
        <label className="rotulo">Cliente cadastrado (vínculo)</label>
        <SeletorCliente
          clienteId={clienteId}
          clienteNome={nomeCadastro}
          aoEscolher={({ id, nome: n }) => { setClienteId(id); setNomeCadastro(n); }}
        />
        <p className="mt-1 text-xs text-mute">
          O vínculo liga o vale ao histórico do cliente; o nome impresso é o do campo acima.
        </p>
      </div>

      {mudouDocumento && (
        <Aviso tipo="alerta">
          Valor ou nome mudaram: o papel que o cliente assinou continua com os antigos.
          Reimprima o vale e colha a assinatura de novo — sem isso, o documento na mão
          dele diz outra coisa.
        </Aviso>
      )}

      {salvar.error ? <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso> : null}

      <button type="button" className="botao-primario w-full" disabled={salvar.isPending}
              onClick={() => salvar.mutate()}>
        {salvar.isPending ? 'Salvando…' : 'Salvar alterações'}
      </button>
    </section>
  );
}
